import React, { useState, useEffect } from 'react';
import Helmet from "react-helmet"
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getAllContests } from '../../features/competition/competitionSlice';
import Leaderboard from "./Leaderboard"
import EmbeddedForm from "../EmbeddedForm"

const SalesforceLogo = () => (
  <div className="bg-white p-3 rounded-md flex items-center justify-center h-14 shadow hover:shadow-md transition-all">
    <div className="text-blue-700 font-bold">Salesforce</div>
  </div>
);

const MuleSoftLogo = () => (
  <div className="bg-white p-3 rounded-md flex items-center justify-center h-14 shadow hover:shadow-md transition-all">
    <div className="text-green-600 font-bold">MuleSoft</div>
  </div>
);

const AWSLogo = () => (
  <div className="bg-white p-3 rounded-md flex items-center justify-center h-14 shadow hover:shadow-md transition-all">
    <div className="text-orange-500 font-bold">AWS</div>
  </div>
);

const TableauLogo = () => (
  <div className="bg-white p-3 rounded-md flex items-center justify-center h-14 shadow hover:shadow-md transition-all">
    <div className="text-blue-500 font-bold">Tableau</div>
  </div>
);

const HerokuLogo = () => (
  <div className="bg-white p-3 rounded-md flex items-center justify-center h-14 shadow hover:shadow-md transition-all">
    <div className="text-purple-600 font-bold">Heroku</div>
  </div>
);

const CompeteHome = () => { 

  const getMetaDescription = () => {
    switch(activeTab) {
      case 'upcoming':
        return 'Join upcoming Salesforce coding competitions, test your Apex skills, and win prizes. View active challenges and register today.';
      case 'past':
        return 'Explore past Salesforce coding competitions, view results, and learn from previous challenges to prepare for future contests.';
      case 'leaderboard':
        return 'Check out the top Salesforce developers in our global leaderboard. Compete and improve your ranking in our coding competitions.';
      case 'notify':
        return 'Sign up for notifications about new Salesforce competitions, results announcements, and exclusive developer opportunities.';
      default:
        return 'Test your Salesforce Apex skills, compete with others, and win prizes in our coding competitions.';
    }
  };
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { competitions, showSpinner } = useSelector((state) => state.competitions);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    dispatch(getAllContests());
    
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  // Categorize competitions into upcoming and past
  const now = new Date();
  const upcomingCompetitions = competitions?.filter(
    (comp) => new Date(comp.enddate) >= now
  ) || [];
  
  const pastCompetitions = competitions?.filter(
    (comp) => new Date(comp.enddate) < now
  ) || [];

  // Featured competition is the closest upcoming one
  const featuredCompetition = upcomingCompetitions.length > 0 
    ? upcomingCompetitions.sort((a, b) => new Date(a.startdate) - new Date(b.startdate))[0] 
    : null;

  const handleNavigateToCompetition = (tag) => {
    navigate(`/compete/${tag}`);
  };

  // Format date to be more readable
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Calculate time remaining for upcoming competitions
  const getTimeRemaining = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (now > end) return 'Completed';
    if (now >= start) return 'Started';
    
    const diff = start - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days} day${days !== 1 ? 's' : ''} remaining`;
    
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return `${hours} hour${hours !== 1 ? 's' : ''} remaining`;
  };

  // Determine status of competition
  const getCompetitionStatus = (comp) => {
    const now = new Date();
    const startDate = new Date(comp.startdate);
    const endDate = new Date(comp.enddate);
    
    if (now < startDate) return 'Upcoming';
    if (now >= startDate && now <= endDate) return 'Active';
    return 'Completed';
  };

  // Get appropriate status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return 'text-lime-400 bg-lime-500/20';
      case 'Upcoming': return 'text-blue-400 bg-blue-500/20';
      case 'Completed': return 'text-zinc-400 bg-zinc-500/20';
      default: return 'text-zinc-400 bg-zinc-500/20';
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900 font-mono text-zinc-100">

<Helmet>
        <title>
          {activeTab === 'upcoming' ? 'Upcoming & Active Salesforce Coding Competitions' :
           activeTab === 'past' ? 'Past Salesforce Coding Competitions' :
           activeTab === 'leaderboard' ? 'Global Leaderboard - Salesforce Coding Competitions' :
           'Salesforce Coding Competitions - Challenge Your Apex Skills'}
        </title>
        <meta name="description" content={getMetaDescription()} />
        <meta name="keywords" content="salesforce, apex, coding competition, developers, programming challenge, coding contest" />
        <link rel="canonical" href={window.location.href} />
        <meta property="og:title" content="Salesforce Coding Competitions" />
        <meta property="og:description" content={getMetaDescription()} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Salesforce Coding Competitions" />
        <meta name="twitter:description" content={getMetaDescription()} />
      </Helmet>


      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 pt-6 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-cyan-400 mb-4">
            Salesforce Coding Competitions
          </h1>
          <p className="text-zinc-300 max-w-3xl mx-auto">
            Test your Salesforce Apex skills, compete with others, and win prizes.
          </p>
        </div>
      
        {/* Sponsors Section - Integrated with header but more balanced */}
        {/* <div className="max-w-6xl mx-auto px-4 mt-12">
          <h2 className="text-center text-xl font-bold text-zinc-200 mb-4">Our Sponsors</h2>
          <p className="text-center text-zinc-400 mb-8 max-w-2xl mx-auto">
            Camp Apex competitions are made possible by our amazing partners who share our passion for building the next generation of Salesforce developers.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 max-w-4xl mx-auto">
            <SalesforceLogo />
            <MuleSoftLogo />
            <AWSLogo />
            <TableauLogo />
            <HerokuLogo />
          </div>
        </div> */}

        {/* <div className="max-w-6xl mx-auto px-4 mt-12">
          <h2 className="text-center text-xl font-bold text-zinc-200 mb-4">Our Sponsors</h2>
          <p className="text-center text-zinc-400 mb-8 max-w-2xl mx-auto">
            Camp Apex competitions are made possible by & prizes are paid for out of pocket by Saman. If your company is interested in sponsoring, reach out!
          </p>
        
        </div> */}
      </div>
      
      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-lime-500/30 via-cyan-500/30 to-purple-500/30"></div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showSpinner ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-12 h-12 border-4 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="border-b border-zinc-800 mb-6">
              <nav className="flex space-x-8">
                <button
                  className={`py-2 px-1 text-sm font-medium border-b-2 ${
                    activeTab === 'upcoming'
                      ? 'border-lime-500 text-lime-400'
                      : 'border-transparent text-zinc-400 hover:text-zinc-300 hover:border-zinc-700'
                  } transition-all`}
                  onClick={() => setActiveTab('upcoming')}
                >
                  Upcoming & Active
                </button>
                <button
                  className={`py-2 px-1 text-sm font-medium border-b-2 ${
                    activeTab === 'past'
                      ? 'border-lime-500 text-lime-400'
                      : 'border-transparent text-zinc-400 hover:text-zinc-300 hover:border-zinc-700'
                  } transition-all`}
                  onClick={() => setActiveTab('past')}
                >
                  Past Competitions
                </button>
                <button 
      className={`py-2 px-1 text-sm font-medium border-b-2 ${
        activeTab === 'leaderboard'
          ? 'border-lime-500 text-lime-400'
          : 'border-transparent text-zinc-400 hover:text-zinc-300 hover:border-zinc-700'
      } transition-all`}
      onClick={() => setActiveTab('leaderboard')}
    >
      Leaderboard
    </button>
    <button 
      className={`py-2 px-1 text-sm font-medium border-b-2 ${
        activeTab === 'notify'
          ? 'border-lime-500 text-lime-400'
          : 'border-transparent text-zinc-400 hover:text-zinc-300 hover:border-zinc-700'
      } transition-all`}
      onClick={() => setActiveTab('notify')}
    >
      Get Notified
    </button>
              </nav>
            </div>


           

            {/* Active Competitions */}
            {activeTab === 'upcoming' && (
              <section className="mb-12">
                <h2 className="flex items-center gap-2 text-xl font-bold text-white mb-6">
                  <span className="w-1.5 h-6 bg-lime-500 rounded-sm"></span>
                  <span>Upcoming & Active Competitions</span>
                </h2>
                
                {upcomingCompetitions.length === 0 ? (
                  <div className="bg-zinc-800/20 rounded-lg p-8 text-center">
                    <p className="text-zinc-400">No upcoming competitions at the moment.</p>
                    <p className="text-zinc-500 text-sm mt-2">Check back soon for new challenges!</p>
                  </div>
                ) : (
                  <div className="grid gap-5">
                    {upcomingCompetitions.map((competition) => {
                      const status = getCompetitionStatus(competition);
                      return (
                        <div 
                          key={competition._id}
                          className="bg-zinc-800/20 rounded-lg p-5 hover:bg-zinc-800/40 transition-all cursor-pointer group"
                          onClick={() => handleNavigateToCompetition(competition.tag)}
                        >
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3">
                            <h3 className="text-lg font-medium text-white mb-1 sm:mb-0">{competition.name}</h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm ${getStatusColor(status)}`}>
                              {status}
                            </span>
                          </div>
                          <p className="text-zinc-400 mb-3 line-clamp-2">{competition.description || "Test your Salesforce skills in this exciting competition!"}</p>
                          <div className="flex flex-wrap gap-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm bg-zinc-800 text-zinc-300">
                              {formatDate(competition.startdate)}
                            </span>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm bg-zinc-800 text-zinc-300">
                              {getTimeRemaining(competition.startdate, competition.enddate)}
                            </span>
                            <button 
                              className="ml-auto px-3 py-1 bg-transparent border border-lime-600 hover:bg-lime-600/20 text-lime-400 text-sm rounded-md transition-all hidden sm:block group-hover:block"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigateToCompetition(competition.tag);
                              }}
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {/* Past Competitions */}
            {activeTab === 'past' && (
              <section>
                <h2 className="flex items-center gap-2 text-xl font-bold text-zinc-400 mb-6">
                  <span className="w-1.5 h-6 bg-zinc-600 rounded-sm"></span>
                  <span>Past Competitions</span>
                </h2>
                
                {pastCompetitions.length === 0 ? (
                  <div className="bg-zinc-800/20 rounded-lg p-8 text-center">
                    <p className="text-zinc-400">No past competitions to display.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pastCompetitions.map((competition) => (
                      <div 
                        key={competition._id}
                        className="bg-zinc-800/10 rounded-lg p-4 hover:bg-zinc-800/30 transition-all cursor-pointer"
                        onClick={() => handleNavigateToCompetition(competition.tag)}
                      >
                        <h3 className="text-lg font-medium text-zinc-300 mb-2">{competition.name}</h3>
                        <p className="text-zinc-500 mb-3 text-sm line-clamp-2">
                          {competition.description || "A past Salesforce coding competition."}
                        </p>
                        <div className="flex justify-between">
                          <span className="text-xs text-zinc-500">{formatDate(competition.enddate)}</span>
                          <span className="text-xs text-zinc-400">View Results</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

{activeTab === 'leaderboard' && (
  <section>
    <h2 className="flex items-center gap-2 text-xl font-bold text-white mb-6">
      <span className="w-1.5 h-6 bg-lime-500 rounded-sm"></span>
      <span>Global Leaderboard</span>
    </h2>
    
    <div className="bg-zinc-800/20 rounded-lg p-5">
      <Leaderboard />
    </div>
  </section>
)}

{activeTab === 'notify' && (
  <section>
   <div className="mb-8 bg-zinc-800/30 rounded-lg overflow-hidden border border-zinc-700/50">
              <div className="p-6">
                <EmbeddedForm 
                  subheading="Stay updated on new competitions and results" 
                  formId="d93390eca5" 
                />
              </div>
            </div>

  </section>
)}





          </>
        )}
      </main>
    </div>
  );
};

export default CompeteHome;